<template>
  <div class="content">
    <div class="module-viewer-wrap">
      <card class="card no-border-card" no-body>
        <div class="glad-module-viewer">

          <div v-if="loading" class="glad-loading">
            <span class="glad-spinner" aria-label="Loading module"></span>
          </div>

          <template v-else-if="module">
            <!-- Breadcrumb -->
            <nav class="glad-breadcrumb">
              <router-link to="/glad/pathways" class="glad-breadcrumb__link">My Pathways</router-link>
              <span class="glad-breadcrumb__sep">›</span>
              <router-link :to="'/glad/pathways/' + module.pathway.id" class="glad-breadcrumb__link">
                {{ module.pathway.title }}
              </router-link>
            </nav>

            <!-- Header -->
            <header class="glad-module-header">
              <div>
                <span class="glad-eyebrow">
                  Module {{ module.position }} of {{ module.total }}
                </span>
                <h1 class="glad-module-title">{{ module.course ? module.course.name : 'Module' }}</h1>
                <p v-if="module.course && module.course.length" class="glad-module-meta">
                  {{ module.course.length }}
                </p>
              </div>
              <span class="glad-status-pill" :class="statusClass(module.module_status)">
                {{ statusLabel(module.module_status) }}
              </span>
            </header>

            <p
              v-if="module.course && module.course.description"
              class="glad-module-description"
              v-html="module.course.description">
            </p>

            <!-- Resources -->
            <section v-if="module.resources && module.resources.length" class="glad-resources">
              <h2 class="glad-resources__heading">Module materials</h2>

              <div
                v-for="res in module.resources"
                :key="res.id"
                class="glad-resource">

                <!-- Secure PDF / file via signed URL -->
                <template v-if="res.is_secure">
                  <div v-if="!signedUrls[res.id]" class="glad-resource__loading">
                    <span class="glad-spinner glad-spinner--sm"></span>
                    <span>Preparing protected document…</span>
                  </div>
                  <SecurePDFViewer
                    v-else
                    :resource-id="res.id"
                    :access-mode="signedUrls[res.id].access_mode"
                    :user-email="userEmail"
                    :title="res.name" />
                </template>

                <!-- YouTube embed -->
                <template v-else-if="isYouTube(res.url)">
                  <div class="glad-resource__title">{{ res.name }}</div>
                  <div class="glad-embed">
                    <iframe
                      :src="youTubeEmbedUrl(res.url)"
                      :title="res.name"
                      frameborder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowfullscreen>
                    </iframe>
                  </div>
                </template>

                <!-- Plain external link -->
                <template v-else>
                  <div class="glad-resource__title">{{ res.name }}</div>
                  <a class="glad-btn glad-btn--ghost" :href="res.url" target="_blank" rel="noopener noreferrer">
                    Open resource
                    <svg viewBox="0 0 20 20" fill="currentColor" width="14" height="14" aria-hidden="true">
                      <path d="M11 3a1 1 0 100 2h2.586l-6.293 6.293a1 1 0 101.414 1.414L15 6.414V9a1 1 0 102 0V4a1 1 0 00-1-1h-5z"/>
                      <path d="M5 5a2 2 0 00-2 2v8a2 2 0 002 2h8a2 2 0 002-2v-3a1 1 0 10-2 0v3H5V7h3a1 1 0 000-2H5z"/>
                    </svg>
                  </a>
                </template>
              </div>
            </section>

            <p v-else class="glad-empty-resources">
              No materials attached to this module yet.
            </p>

            <!-- Discussion thread (§10) -->
            <DiscussionThread
              :pathway-id="module.pathway.id"
              :module-id="module.id" />

            <!-- Footer actions -->
            <footer class="glad-module-footer">
              <div class="glad-module-footer__left">
                <router-link
                  v-if="module.prev_module_id"
                  :to="'/glad/pathways/' + module.pathway.id + '/modules/' + module.prev_module_id"
                  class="glad-btn glad-btn--ghost">
                  ← Previous
                </router-link>
              </div>

              <div class="glad-module-footer__right">
                <button
                  v-if="module.module_status !== 'completed'"
                  type="button"
                  class="glad-btn"
                  :disabled="completing || module.module_status === 'locked'"
                  @click="markComplete">
                  {{ completing ? 'Saving…' : 'Mark complete' }}
                </button>

                <router-link
                  v-if="module.next_module_id && module.module_status === 'completed'"
                  :to="'/glad/pathways/' + module.pathway.id + '/modules/' + module.next_module_id"
                  class="glad-btn">
                  Next module →
                </router-link>

                <router-link
                  v-else-if="!module.next_module_id && module.module_status === 'completed'"
                  :to="'/glad/pathways/' + module.pathway.id"
                  class="glad-btn">
                  Back to pathway
                </router-link>
              </div>
            </footer>
          </template>

          <div v-else class="glad-empty">
            <p>Module not found.</p>
            <router-link to="/glad/pathways" class="glad-btn glad-btn--ghost">Back to pathways</router-link>
          </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import SecurePDFViewer from '@/components/glad/SecurePDFViewer.vue';
import DiscussionThread from '@/components/glad/DiscussionThread.vue';

export default {
  name: 'ModuleViewer',
  components: { SecurePDFViewer, DiscussionThread },
  data() {
    return {
      loading: true,
      completing: false,
      module: null,
      signedUrls: {}, // resourceId → { url, access_mode }
      userEmail: '',
    };
  },
  created() {
    if (typeof localStorage !== 'undefined') {
      this.userEmail = localStorage.getItem('hot-email') || '';
    }
    this.fetchModule();
  },
  watch: {
    '$route.params.moduleId'() {
      this.signedUrls = {};
      this.fetchModule();
    },
  },
  methods: {
    fetchModule() {
      this.loading = true;
      const { pathwayId, moduleId } = this.$route.params;
      this.$http.get(`glad/pathways/${pathwayId}/modules/${moduleId}`)
        .then(resp => {
          this.module = resp.data;
          this.fetchSignedUrls();
        })
        .catch(() => { this.module = null; })
        .finally(() => { this.loading = false; });
    },
    fetchSignedUrls() {
      if (!this.module || !this.module.resources) return;
      this.module.resources
        .filter(r => r.is_secure && r.id)
        .forEach(r => {
          this.$http.get('glad/secure-url', { params: { resource_id: r.id } })
            .then(resp => {
              this.$set(this.signedUrls, r.id, {
                url: resp.data.url,
                access_mode: resp.data.access_mode || 'view_only',
              });
            })
            .catch(() => { /* leave it in "preparing…" state */ });
        });
    },
    markComplete() {
      if (!this.module) return;
      this.completing = true;
      const { pathwayId, moduleId } = this.$route.params;
      this.$http.post(`glad/pathways/${pathwayId}/modules/${moduleId}/complete`)
        .then(resp => {
          this.module.module_status = 'completed';
          if (resp.data && resp.data.next_module_id) {
            this.module.next_module_id = resp.data.next_module_id;
          }
        })
        .catch(() => {})
        .finally(() => { this.completing = false; });
    },
    isYouTube(url) {
      if (!url) return false;
      return /(?:youtube\.com|youtu\.be)/i.test(url);
    },
    youTubeEmbedUrl(url) {
      if (!url) return '';
      const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
      return m ? `https://www.youtube.com/embed/${m[1]}` : url;
    },
    statusLabel(s) {
      return { locked: 'Locked', in_progress: 'In progress', completed: 'Complete' }[s] || s;
    },
    statusClass(s) {
      return {
        'glad-status-pill--neutral': s === 'locked',
        'glad-status-pill--active': s === 'in_progress',
        'glad-status-pill--success': s === 'completed',
      };
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@/assets/sass/glad-tokens.scss';

.module-viewer-wrap { width: 100%; }
.module-viewer-wrap ::v-deep > .card.no-border-card {
  border: 0;
  box-shadow: none;
}
.glad-module-viewer {
  padding: 1.5rem;
  font-family: var(--glad-font-sans);
}

.glad-loading {
  display: flex;
  justify-content: center;
  padding: 4rem 0;
}
.glad-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid var(--glad-line);
  border-top-color: var(--glad-ink);
  border-radius: 50%;
  animation: glad-spin 0.7s linear infinite;
  display: inline-block;
  &--sm { width: 16px; height: 16px; border-width: 2px; }
}
@keyframes glad-spin { to { transform: rotate(360deg); } }

.glad-breadcrumb {
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.85rem;
  color: var(--glad-text-soft);
}
.glad-breadcrumb__link {
  color: var(--glad-text-soft);
  text-decoration: none;
  &:hover { color: var(--glad-ink); }
}
.glad-breadcrumb__sep { color: var(--glad-line-strong, #d2cfc6); }

.glad-module-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1.5rem;
  padding-bottom: 1.25rem;
  margin-bottom: 1.5rem;
  border-bottom: 1px solid var(--glad-line);
}
.glad-eyebrow {
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--glad-accent);
  display: block;
  margin-bottom: 0.35rem;
}
.glad-module-title {
  font-family: var(--glad-font-serif);
  font-size: 1.55rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0 0 0.3rem;
  line-height: 1.25;
}
.glad-module-meta {
  font-size: 0.82rem;
  color: var(--glad-text-muted);
  margin: 0;
}

.glad-status-pill {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.2rem 0.6rem;
  border-radius: 999px;
  flex-shrink: 0;
  &--neutral { background: var(--glad-paper-2); color: var(--glad-text-soft); }
  &--active  { background: #fef3c7; color: #92400e; }
  &--success { background: #dcfce7; color: #15803d; }
}

.glad-module-description {
  font-size: 0.92rem;
  color: var(--glad-text-soft);
  line-height: 1.65;
  margin: 0 0 2rem;
  // sanitized HTML from course description — keep paragraphs readable
  ::v-deep p { margin: 0 0 0.85rem; }
  ::v-deep ul, ::v-deep ol { margin: 0 0 0.85rem 1.25rem; }
}

.glad-resources__heading {
  font-family: var(--glad-font-serif);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--glad-ink);
  margin: 0 0 1rem;
}

.glad-resource {
  margin-bottom: 1.5rem;

  &:last-child { margin-bottom: 0; }
}
.glad-resource__title {
  font-size: 0.92rem;
  font-weight: 600;
  color: var(--glad-ink);
  margin-bottom: 0.6rem;
}
.glad-resource__loading {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  padding: 1.5rem;
  background: var(--glad-paper-2);
  border-radius: var(--glad-radius);
  color: var(--glad-text-soft);
  font-size: 0.85rem;
}

.glad-embed {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  border-radius: var(--glad-radius);
  overflow: hidden;
  background: #000;
  iframe {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
}

.glad-empty-resources {
  padding: 1.5rem;
  background: var(--glad-paper-2);
  border-radius: var(--glad-radius);
  color: var(--glad-text-soft);
  font-size: 0.85rem;
  text-align: center;
  margin: 0 0 2rem;
}

.glad-module-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--glad-line);
}
.glad-module-footer__right {
  display: flex;
  gap: 0.6rem;
  align-items: center;
}

.glad-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.55rem 1.25rem;
  background: var(--glad-ink);
  color: #fff;
  border: none;
  border-radius: var(--glad-radius);
  font-family: var(--glad-font-sans);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: opacity 0.15s ease;
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }

  &--ghost {
    background: transparent;
    color: var(--glad-text-soft);
    border: 1.5px solid var(--glad-line);
    &:hover { background: var(--glad-paper-2); opacity: 1; }
  }
}

.glad-empty {
  text-align: center;
  padding: 4rem 1rem;
  color: var(--glad-text-soft);
  p { margin-bottom: 1rem; }
}
</style>
